import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Users, Server, LayoutDashboard, BookOpen, ChevronDown, Settings, MessageSquare, Megaphone, Menu, History, DollarSign } from 'lucide-react';

export const AdminDashboard: React.FC = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation(); // Para la key y forzar el refresco

    // Estados solo para la UI del menú, ya no para el contenido
    const [isManagementMenuOpen, setManagementMenuOpen] = useState(true);
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/admin/login', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    if (!isAuthenticated) {
        return null; // O un spinner de carga
    }

    // El título ahora se puede determinar por la URL si se quiere, o dejarlo genérico
    const getTitle = () => {
        const path = location.pathname.split('/').pop() || 'dashboard';
        const titles: { [key: string]: string } = {
            dashboard: 'Resumen General',
            servers: 'Gestión de Servidores',
            clients: 'Gestión de Clientes',
            tickets: 'Tickets de Soporte',
            announcements: 'Gestión de Anuncios',
            logs: 'Logs de Actividad',
            accounting: 'Contabilidad'
        };
        return titles[path] || 'Panel de Administración';
    };

    return (
        <div className="flex min-h-screen bg-gray-100 font-sans">
            <aside className={`fixed lg:relative z-20 h-full w-64 bg-slate-900 text-gray-300 flex-col shadow-2xl transition-transform transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
                <div className="flex items-center justify-center h-20 border-b border-slate-700">
                    <img src="/logo-lisa-5.png" alt="Logo Principal" className="h-12" />
                </div>
                <nav className="flex-1 px-4 py-6 space-y-2">
                    <NavLink to="/admin" end className={({ isActive }) => `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                        <LayoutDashboard className="h-5 w-5 mr-3" /> Dashboard
                    </NavLink>
                    
                    {/* Menú desplegable */}
                    <div>
                        <button onClick={() => setManagementMenuOpen(!isManagementMenuOpen)} className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-slate-800 hover:text-white rounded-lg">
                            <span className="flex items-center"><Settings className="h-5 w-5 mr-3" /> Gestión</span>
                            <ChevronDown className={`w-5 h-5 transition-transform ${isManagementMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isManagementMenuOpen && (
                            <div className="mt-2 pl-6 space-y-2 border-l border-slate-700 ml-4">
                                <NavLink to="/admin/servers" className={({ isActive }) => `flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-slate-800 hover:text-white'}`}>
                                    <Server className="h-5 w-5 mr-3" /> Servidores
                                </NavLink>
                                <NavLink to="/admin/clients" className={({ isActive }) => `flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-md' : 'hover:bg-slate-800 hover:text-white'}`}>
                                    <Users className="h-5 w-5 mr-3" /> Clientes
                                </NavLink>
                            </div>
                        )}
                    </div>
                    
                    <NavLink to="/admin/tickets" className={({ isActive }) => `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                        <MessageSquare className="h-5 w-5 mr-3" /> Tickets de Soporte
                    </NavLink>
                    <NavLink to="/admin/announcements" className={({ isActive }) => `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                        <Megaphone className="h-5 w-5 mr-3" /> Anuncios
                    </NavLink>
                    <NavLink to="/admin/logs" className={({ isActive }) => `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                        <History className="h-5 w-5 mr-3" /> Logs de Actividad
                    </NavLink>
                    <NavLink to="/admin/accounting" className={({ isActive }) => `flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-blue-600 text-white shadow-lg' : 'hover:bg-slate-800 hover:text-white'}`}>
                        <DollarSign className="h-5 w-5 mr-3" /> Contabilidad
                    </NavLink>
                </nav>
                <div className="px-4 py-4 border-t border-slate-700">
                    <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-slate-800 hover:text-white rounded-lg flex items-center">
                        <LogOut className="h-5 w-5 mr-3" />
                        <span>Cerrar Sesión ({user?.username})</span>
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col">
                <header className="bg-white h-20 flex items-center justify-between px-4 lg:px-8 border-b border-gray-200">
                    <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-600">
                        <Menu className="h-6 w-6" />
                    </button>
                    <h1 className="text-xl lg:text-2xl font-bold text-gray-800 capitalize">
                        {getTitle()}
                    </h1>
                    <div className="lg:hidden w-8"></div>
                </header>
                <main className="flex-1 p-4 md:p-8 overflow-y-auto">
                    {/* El Outlet renderiza el componente de la ruta activa y la key fuerza el refresco */}
                    <Outlet key={location.pathname} />
                </main>
            </div>

            {isSidebarOpen && (
                <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black opacity-50 z-10 lg:hidden"></div>
            )}
        </div>
    );
};
